/**
 * bg-options-preview.js — SVG overlay live-preview engine.
 * DRAFT ONLY. See SPEC_bg-options-engine.md §5 and engraving-zone-model.md.
 *
 * Listens for `bg:selection-changed` and `bg:file-selected` (dispatched by bg-options.js)
 * and updates an absolutely-positioned SVG overlay on top of the PDP hero image.
 * Progressive enhancement: if a product has no preview_zones metafield, this file no-ops
 * cleanly (SPEC §5.7) — never blocks purchase.
 */
(function () {
  'use strict';

  var FILL_MAP = {
    'etched': '#5a5a5a',
    'silver_fill': '#c8c8c8',
    'gold_fill': '#c99a2e',
    'silver-fill': '#c8c8c8',
    'gold-fill': '#c99a2e'
  };

  // Engine selections are keyed by option slug (bottle-engraving-design-type,
  // bottle-text-line-1, ...) — pick the first non-empty selection whose key
  // matches, with the draft-era generic keys kept as fallback.
  function pick(selections, re) {
    for (var k in selections) {
      if (re.test(k) && selections[k]) return selections[k];
    }
    return null;
  }

  function PreviewOverlay(root) {
    this.root = root;
    this.heroImg = this.resolveHero();
    this.zones = [];
    this.overlayEls = {}; // zone_key -> { svg, text, image }
    this.heroImageRef = root.getAttribute('data-hero-image-ref') || '';
    this.bind();
  }

  // Enterprise E.1 renders hidden duplicates of img.product-image (zoom/preload
  // slides) — the overlay must anchor to the VISIBLE hero, resolved lazily
  // because sliders initialize after DOMContentLoaded.
  PreviewOverlay.prototype.resolveHero = function () {
    var cands = document.querySelectorAll('img.product-image, .product__media img, [data-product-media] img, img.bg-hero-fallback');
    for (var i = 0; i < cands.length; i++) {
      if (cands[i].offsetParent !== null && cands[i].getBoundingClientRect().width > 100) return cands[i];
    }
    return cands[0] || null;
  };

  PreviewOverlay.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('bg:selection-changed', function (e) {
      self.zones = e.detail.zones || [];
      self.render(e.detail.selections, e.detail.feeState);
    });
    this.root.addEventListener('bg:file-selected', function (e) {
      self.renderImageZone(e.detail.key, e.detail.objectUrl);
    });
    if (window.ResizeObserver && this.heroImg) {
      var ro = new ResizeObserver(function () { self.repositionAll(); });
      ro.observe(this.heroImg);
    }
  };

  PreviewOverlay.prototype.ensureOverlay = function (zone) {
    if (!this.heroImg || this.heroImg.offsetParent === null) {
      // stale/hidden hero (slider re-init or duplicate slide) — re-resolve and
      // drop any overlays anchored to the old node
      var fresh = this.resolveHero();
      if (fresh !== this.heroImg) {
        Object.keys(this.overlayEls).forEach(function (k) {
          var e = this.overlayEls[k];
          if (e && e.svg && e.svg.parentElement) e.svg.parentElement.removeChild(e.svg);
        }, this);
        this.overlayEls = {};
        this.heroImg = fresh;
      }
    }
    if (this.overlayEls[zone.zone_key]) return this.overlayEls[zone.zone_key];
    if (!this.heroImg) return null;

    // Drift-guard: suppress overlay if the hero image doesn't match the zone's calibrated ref
    if (zone.hero_image_ref && this.heroImg.currentSrc && this.heroImg.currentSrc.indexOf(zone.hero_image_ref) === -1) {
      console.warn('[bg-options-preview] hero_image_ref mismatch for zone "' + zone.zone_key + '" — suppressing overlay, falling back to text-only preview.');
      return null;
    }

    // Anchor to the theme's SIZED media container, never the img's direct
    // parent: on Enterprise the parent is a <picture> (inline, auto-sized) and
    // forcing position:relative on it re-parents the absolutely-positioned
    // img-fit image -> hero collapses to 0 width (found 2026-07-17).
    var wrap = this.heroImg.closest('.media--cover, .media, .product__media') || this.heroImg.parentElement;
    if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';

    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('aria-hidden', 'true'); // decorative/duplicate — real value lives in the input's accessible name (SPEC §9)
    svg.classList.add('bg-preview-overlay');
    svg.style.position = 'absolute';
    svg.style.pointerEvents = 'none';
    svg.style.left = zone.anchor_x_pct + '%';
    svg.style.top = zone.anchor_y_pct + '%';
    svg.style.width = zone.width_pct + '%';
    svg.style.transform = 'translate(-50%,-50%) rotate(' + (zone.rotation_deg || 0) + 'deg)';
    svg.setAttribute('viewBox', '0 0 200 60');

    var text = document.createElementNS(svgNS, 'text');
    text.setAttribute('x', '100');
    text.setAttribute('y', '30');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'middle');
    text.setAttribute('font-family', zone.font_family || 'Georgia, serif');
    text.setAttribute('font-size', '18');
    text.classList.add('engr');
    svg.appendChild(text);

    wrap.appendChild(svg);
    var entry = { svg: svg, text: text, zone: zone };
    this.overlayEls[zone.zone_key] = entry;
    return entry;
  };

  PreviewOverlay.prototype.render = function (selections, feeState) {
    var self = this;
    this.zones.forEach(function (zone) {
      var entry = self.ensureOverlay(zone);
      if (!entry) return;
      var designType = pick(selections, /(^|-)design-type$/) || selections.design_type;
      var line1 = pick(selections, /(^|-)text-line-1$|^line1$/) || '';
      if ((!designType || designType === 'none') && !line1) {
        entry.svg.style.display = 'none';
        return;
      }
      entry.svg.style.display = '';
      var finishKey = pick(selections, /(^|-)(text|logo|design)-finish$/) ||
        selections.finish || selections.logo_finish || 'etched';
      entry.text.setAttribute('fill', FILL_MAP[String(finishKey).toLowerCase()] || '#5a5a5a');
      entry.text.textContent = String(line1).slice(0, zone.max_preview_chars || 20);
      var font = pick(selections, /(^|-)font$/) || selections.font;
      entry.text.setAttribute('font-family', font || zone.font_family || 'Georgia, serif');
    });
  };

  PreviewOverlay.prototype.renderImageZone = function (optionKey, objectUrl) {
    var primaryZone = this.zones[0];
    if (!primaryZone) return;
    var entry = this.ensureOverlay(primaryZone);
    if (!entry) return;
    var svgNS = 'http://www.w3.org/2000/svg';
    var existing = entry.svg.querySelector('image');
    if (existing) existing.remove();
    var img = document.createElementNS(svgNS, 'image');
    img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', objectUrl);
    img.setAttribute('x', '60');
    img.setAttribute('y', '5');
    img.setAttribute('width', '80');
    img.setAttribute('height', '50');
    img.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    entry.svg.appendChild(img);
    entry.svg.style.display = '';
  };

  PreviewOverlay.prototype.repositionAll = function () {
    // Positions are percentage-based (CSS left/top/width), so ResizeObserver only needs to
    // trigger a reflow-safe no-op here; kept as an explicit hook for future raster-based zones.
  };

  function boot() {
    document.querySelectorAll('[data-bg-options-root]').forEach(function (root) {
      new PreviewOverlay(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
